import { useExodus, useSelectors } from '@exodus/headless-react';
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useToAssets } from '../../context/assets.js';
import useFromAmount from '../../hooks/use-from-amount.js';
import useFromAsset from '../../hooks/use-from-asset.js';
import useToAsset from '../../hooks/use-to-asset.js';
import useToAmount from '../../hooks/use-to-amount.js';
import useToWalletAccount from '../../hooks/use-to-wallet-account.js';
import useToSideLoading from '../../hooks/use-to-side-loading.js';
import { useAddingCustomTokenContext } from '../../context/adding-custom-token.js';
import useTrackAssetConfigureEvent from '../../hooks/analytics/use-track-configure-asset.js';
import { isTokenAsset, isExchangeAsset } from '../../types.js';
const ToAsset = ({ Component, className, style, onPress }) => {
    const [toAsset, setToAsset] = useToAsset();
    const [fromAsset, setFromAsset] = useFromAsset();
    const [, setFromAmount] = useFromAmount();
    const resetToAmountValue = useToAmount()[2];
    const selectors = useSelectors();
    const exodus = useExodus();
    const getIsAssetEnabled = useSelector(selectors.enabledAssets.getIsEnabled);
    const assets = useSelector(selectors.assets.all);
    const [toWalletAccount] = useToWalletAccount();
    const availableToAssets = useToAssets();
    const toSideLoading = useToSideLoading();
    const [addingCustomToken, setAddingCustomToken] = useAddingCustomTokenContext();
    const trackAssetConfigureSwap = useTrackAssetConfigureEvent();
    const maybeEnable = useCallback(async (asset) => {
        const tryAdd = (address, baseAssetName) => exodus.assets.addAndEnableToken(address, baseAssetName).catch(() => {
            console.warn('failed to add custom token', asset);
            return undefined;
        });
        if (isTokenAsset(asset)) {
            setAddingCustomToken(asset);
            const address = asset.assetId;
            return tryAdd(address, asset.baseAsset.name);
        }
        if ('isBuiltIn' in asset && asset.isBuiltIn) {
            setAddingCustomToken(asset);
            await exodus.assets.enable([asset.name]).catch((err) => {
                console.warn('failed to enable asset', asset, err);
            });
            return asset.name;
        }
        if (isExchangeAsset(asset)) {
            const assetName = assets[asset.network].name;
            const address = Object.values(asset.meta)[0];
            setAddingCustomToken({ ...asset, displayName: asset.name, displayTicker: asset.symbol });
            return tryAdd(address, assetName);
        }
    }, [assets, exodus, setAddingCustomToken]);
    const handlePress = useCallback(async () => {
        const asset = await onPress?.({
            side: 'to',
            walletAccount: toWalletAccount.toString(),
            assets: availableToAssets.value || [],
        });
        if (!asset)
            return;
        void trackAssetConfigureSwap();
        const isAssetEnabled = getIsAssetEnabled(asset.name);
        let enabledAssetName = isAssetEnabled ? asset.name : undefined;
        if (!isAssetEnabled) {
            enabledAssetName = await maybeEnable(asset);
            setAddingCustomToken(null);
        }
        if (!enabledAssetName)
            return;
        const enabledAsset = await exodus.assets.getAsset(enabledAssetName);
        const currentToAsset = toAsset;
        setToAsset(enabledAsset.name);
        resetToAmountValue();
        if (fromAsset.name === asset.name) {
            setFromAsset(currentToAsset.name);
            setFromAmount(fromAsset.currency.ZERO);
        }
    }, [
        onPress,
        toWalletAccount,
        availableToAssets.value,
        trackAssetConfigureSwap,
        getIsAssetEnabled,
        exodus.assets,
        toAsset,
        setToAsset,
        resetToAmountValue,
        fromAsset.name,
        fromAsset.currency.ZERO,
        maybeEnable,
        setAddingCustomToken,
        setFromAsset,
        setFromAmount,
    ]);
    if (Component) {
        return (React.createElement(Component, { side: "to", asset: addingCustomToken || toAsset, loading: toSideLoading, className: className, style: style, onPress: handlePress }));
    }
    return null;
};
export default ToAsset;
